import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where
} from "firebase/firestore";

import { db } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import {
  getAccessRequestType,
  redirectToJobBankLogin
} from "./jobBankRole";

import "./accessRequests.css";

function getParam(name) {
  return new URLSearchParams(window.location.search).get(name) || "";
}

function clean(value) {
  return String(value || "").trim();
}

export default function AccessRequest() {
  const { currentUser, loadingProfile } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingRequest, setExistingRequest] = useState(null);
  const [message, setMessage] = useState("");

  const accessKey = getParam("accessKey");
  const configuredType = getAccessRequestType(accessKey);

  const requestedRole = clean(
    getParam("role") || configuredType?.requestedRole
  );

  const requestedAccess = clean(
    getParam("access") || configuredType?.label
  );

  const sourcePath = clean(
    getParam("from") || configuredType?.destination || "/jobbank"
  );

  const requestSummary = useMemo(() => ({
    accessKey: accessKey || configuredType?.accessKey || "custom-request",
    requestedRole,
    requestedAccess,
    sourcePath
  }), [
    accessKey,
    configuredType,
    requestedRole,
    requestedAccess,
    sourcePath
  ]);

  useEffect(() => {
    if (loadingProfile) {
      return;
    }

    if (!currentUser) {
      redirectToJobBankLogin(
        window.location.pathname + window.location.search
      );
      return;
    }

    async function loadRequestContext() {
      try {
        setLoading(true);

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const data = userSnap.exists()
          ? userSnap.data()
          : {};

        setUserData(data);

        const existingQuery = query(
          collection(db, "accessRequests"),
          where("uid", "==", currentUser.uid),
          where("accessKey", "==", requestSummary.accessKey),
          where("status", "==", "Pending"),
          limit(1)
        );

        const existingSnap = await getDocs(existingQuery);

        if (!existingSnap.empty) {
          const first = existingSnap.docs[0];
          setExistingRequest({
            id: first.id,
            ...first.data()
          });
        }
      } catch (error) {
        console.error("Unable to load access request context:", error);
        setMessage("Unable to load your access request details.");
      } finally {
        setLoading(false);
      }
    }

    loadRequestContext();
  }, [
    currentUser,
    loadingProfile,
    requestSummary.accessKey
  ]);

  async function submitRequest() {
    if (!currentUser) {
      redirectToJobBankLogin(
        window.location.pathname + window.location.search
      );
      return;
    }

    if (!requestedRole || !requestedAccess) {
      setMessage("This access request is missing a requested role or service name.");
      return;
    }

    if (existingRequest) {
      setMessage("You already have a pending request for this access.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      await addDoc(
        collection(db, "accessRequests"),
        {
          uid: currentUser.uid,
          email: userData?.email || currentUser.email || "",
          displayName: [
            userData?.firstName,
            userData?.lastName
          ].filter(Boolean).join(" ") || currentUser.displayName || "",
          firstName: userData?.firstName || "",
          lastName: userData?.lastName || "",
          phone: userData?.phone || "",
          city: userData?.city || "",
          province: userData?.province || "",
          currentJobBankRole: userData?.jobBankRole || "",
          jobRole: userData?.jobRole || "",
          primaryOccupation: userData?.primaryOccupation || "",
          primaryEmploymentGoals: userData?.primaryEmploymentGoals || "",
          accessKey: requestSummary.accessKey,
          requestedAccess,
          requestedRole,
          sourcePath,
          status: "Pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

      setMessage("Your access request has been submitted.");
      setExistingRequest({ status: "Pending" });
    } catch (error) {
      console.error("Unable to submit access request:", error);
      setMessage("Unable to submit your access request.");
    } finally {
      setSaving(false);
    }
  }

  if (loadingProfile || loading) {
    return (
      <div className="accessRequestPage">
        <div className="accessRequestCard">
          <h2>Loading Access Request...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="accessRequestPage">
      <div className="accessRequestCard">
        <p className="accessRequestEyebrow">
          The iSpeakWell Project Job Bank
        </p>

        <h1>Request Access</h1>

        <p>
          You are requesting access to <strong>{requestedAccess}</strong>.
          Approval will assign or update your Job Bank Role to
          <strong> {requestedRole}</strong>.
        </p>

        <div className="accessRequestDetails">
          <div>
            <strong>Name</strong>
            <span>
              {[userData?.firstName, userData?.lastName]
                .filter(Boolean)
                .join(" ") || currentUser?.displayName || "Not set"}
            </span>
          </div>

          <div>
            <strong>Email</strong>
            <span>{userData?.email || currentUser?.email || "Not set"}</span>
          </div>

          <div>
            <strong>Current Role</strong>
            <span>{userData?.jobBankRole || "No Job Bank Role"}</span>
          </div>

          <div>
            <strong>Requested Role</strong>
            <span>{requestedRole}</span>
          </div>

          <div>
            <strong>Requested Access</strong>
            <span>{requestedAccess}</span>
          </div>
        </div>

        {message && (
          <p className="accessRequestMessage">
            {message}
          </p>
        )}

        <div className="accessRequestActions">
          <button
            onClick={submitRequest}
            disabled={saving || !!existingRequest}
          >
            {existingRequest
              ? "Request Pending"
              : saving
                ? "Submitting..."
                : "Submit Access Request"}
          </button>

          <button
            type="button"
            onClick={() => window.location.href = "/jobbank"}
          >
            Return
          </button>
        </div>
      </div>
    </div>
  );
}
